import React from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useRootStore } from "../../store/contexts";

/** Recién pasado este tiempo aparece el modal: una subida rápida no lo hace parpadear. */
const DEMORA_MS = 400;

/**
 * Modal de espera de TODA subida de archivo, con la opción de cancelarla.
 *
 * Existe porque una subida lenta —30 segundos con una conexión mala— no daba
 * ninguna señal: el archivo aparecía en la lista como si ya estuviera, se
 * guardaba, y el servidor rechazaba el formulario por un archivo que todavía no
 * tenía. Mientras está abierto el formulario queda tapado, así que no se puede
 * guardar a mitad de camino, y Cancelar corta la subida.
 *
 * Es uno solo, montado en PageLayout, y escucha las actividades "fileUpload"
 * del ActivityStore: toda subida pasa por apiUploadFile, así que cubre cualquier
 * campo de archivo sin que cada formulario tenga que hacer nada.
 */
export const UploadProgressDialog = observer(() => {
  const { activityStore, langStore } = useRootStore();
  const uploads = activityStore.activeOperations.filter(
    (a) => a.operation === "fileUpload"
  );
  const [now, setNow] = React.useState(() => Date.now());

  // Un reloj propio mientras hay subidas: hace aparecer el modal pasada la
  // demora y mantiene al día los segundos, que son la señal de que sigue viva.
  React.useEffect(() => {
    if (uploads.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [uploads.length]);

  const visibles = uploads.filter((u) => now - u.startedAt >= DEMORA_MS);

  return (
    <Dialog
      open={visibles.length > 0}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      aria-labelledby="upload-progress-title"
    >
      <DialogTitle id="upload-progress-title">
        {langStore.uploadingFileTitle}
      </DialogTitle>
      <DialogContent>
        {visibles.map((u) => (
          <Box
            key={u.id}
            sx={{ display: "flex", alignItems: "center", gap: 2, py: 1 }}
          >
            <CircularProgress size={28} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography noWrap title={u.detail}>
                {langStore.uploadingFileMessage(u.detail ?? "")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.floor((now - u.startedAt) / 1000)} s
              </Typography>
            </Box>
            {u.policy.manualCancellable && (
              <Button onClick={() => activityStore.manualCancel(u.id)}>
                {langStore.cancel}
              </Button>
            )}
          </Box>
        ))}
      </DialogContent>
    </Dialog>
  );
});
